import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { ControlPointStatusView } from '@fsf/types';
import { EnvironmentToken } from '../../../app.config';

const POLL_INTERVAL_MS = 15_000;

interface ControlPointStatusResponse {
  data: ControlPointStatusView[];
  config_error: boolean;
}

/**
 * Cash-desk alarm data client. Root-provided so the Tagesdashboard panel and
 * the standalone control-points status/overview page share one polling
 * instance — and so Vite chunk-splitting cannot drop the provider (see
 * `feedback_angular_services_root_provide.md`). Mirrors DayDashboardService.
 *
 * On poll failure the last-known statuses are KEPT (never blanked) so the
 * always-on safety panel does not silently freeze to empty; consumers read
 * `lastError` + `lastFetchedAt` to surface a stale indicator instead.
 */
@Injectable({ providedIn: 'root' })
export class ControlPointStatusService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #destroyRef = inject(DestroyRef);

  #statuses = new BehaviorSubject<ControlPointStatusView[]>([]);
  public statuses = toSignal(this.#statuses.asObservable(), {
    initialValue: [] as ControlPointStatusView[],
  });

  #configError = new BehaviorSubject<boolean>(false);
  public configError = toSignal(this.#configError.asObservable(), {
    initialValue: false,
  });

  #lastFetchedAt = new BehaviorSubject<number | null>(null);
  public lastFetchedAt = toSignal(this.#lastFetchedAt.asObservable(), {
    initialValue: null,
  });

  #lastError = new BehaviorSubject<HttpErrorResponse | null>(null);
  public lastError = toSignal(this.#lastError.asObservable(), {
    initialValue: null,
  });

  #pollStarted = false;

  /**
   * Begin the 15s poll. Idempotent: repeated calls (e.g. the dashboard panel
   * and the standalone page both starting it) only ever start one stream. The
   * stream tears down with the root injector via takeUntilDestroyed.
   */
  startPolling(): void {
    if (this.#pollStarted) return;
    this.#pollStarted = true;

    timer(0, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.#http
            .get<ControlPointStatusResponse>(
              `${this.#apiUrl}/api/admin/control-points/status`,
            )
            .pipe(
              tap((resp) => {
                this.#statuses.next(resp.data ?? []);
                this.#configError.next(resp.config_error ?? false);
                this.#lastFetchedAt.next(Date.now());
                this.#lastError.next(null);
              }),
              catchError((err: HttpErrorResponse) => {
                // Keep last-known statuses; surface the failure via lastError.
                this.#lastError.next(err);
                return EMPTY;
              }),
            ),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }
}
